import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Icon } from "@iconify/react";
import Common from "../../helper/common";
import {
  respEnum,
  AmericanoTypeEnum,
  AmericanoAccessTypeEnum,
  AmericanoMatchResultEnum,
} from "../../constants/appConstant";
import toast from "react-hot-toast";
import {
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "../../components/ui/avatar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { AppInput, AppSelect } from "../../components/appComponents";
import moment from "moment";

const AmericanoDetails = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [americano, setAmericano] = useState(null);
  const [standings, setStandings] = useState([]);
  const [standingsLoading, setStandingsLoading] = useState(false);
  const [schedule, setSchedule] = useState([]);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [uploadingResults, setUploadingResults] = useState({}); // Track uploading state per match
  const [inlineGrid, setInlineGrid] = useState(null); // { matchId: number, side: "A" | "B" } or null

  const id = searchParams.get("id");

  useEffect(() => {
    if (id) {
      getAmericanoDetails();
      getStandings();
      getSchedule();
    }
  }, [id]);

  const getAmericanoDetails = async () => {
    setLoading(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        `GetAmericanoDetails?id=${id}`,
        {},
        "Get"
      );

      if (response?.status === respEnum.Success && response.data?.[0]) {
        setAmericano(response.data[0]);
      } else {
        toast.error(response.message || "Americano not found");
        navigate("/app/americanos");
      }
    } catch (error) {
      toast.error(error.message || "Error fetching americano details");
      navigate("/app/americanos");
    } finally {
      setLoading(false);
    }
  };

  const getStandings = async () => {
    setStandingsLoading(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        `GetAmericanoStandings?americano_id=${id}`,
        {},
        "Get"
      );

      if (response?.status === respEnum.Success && response.data) {
        // response.data is already a List, not List<List>
        setStandings(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error("Error fetching standings:", error);
    } finally {
      setStandingsLoading(false);
    }
  };

  const getSchedule = async () => {
    setScheduleLoading(true);
    try {
      const response = await Common.ApiService.getInstance().request(
        "GetAmericanoSchedule",
        {
          americano_id: parseInt(id),
        },
        "Post"
      );

      if (response?.status === respEnum.Success) {
        const scheduleData = response.data || [];
        setSchedule(Array.isArray(scheduleData) ? scheduleData : []);
      } else {
        // Schedule might not exist yet, that's okay
        setSchedule([]);
      }
    } catch (error) {
      console.error("Error fetching schedule:", error);
      toast.error("Error loading schedule");
      setSchedule([]);
    } finally {
      setScheduleLoading(false);
    }
  };

  const generateSchedule = async () => {
    try {
      setScheduleLoading(true);
      const response = await Common.ApiService.getInstance().request(
        "GetAmericanoSchedule",
        {
          americano_id: parseInt(id),
        },
        "Post"
      );

      if (response?.status === respEnum.Success) {
        toast.success("Schedule generated successfully!");
        await getSchedule();
        await getAmericanoDetails();
        setActiveTab("schedule");
      } else {
        toast.error(response.message || "Failed to generate schedule");
      }
    } catch (error) {
      toast.error(error.message || "Error generating schedule");
    } finally {
      setScheduleLoading(false);
    }
  };

  const handleTeamClick = (match, side) => {
    // If grid is already open for this match and side, close it
    if (inlineGrid?.matchId === match.id && inlineGrid?.side === side) {
      setInlineGrid(null);
      return;
    }
    // Open grid for this match and side
    setInlineGrid({ matchId: match.id, side });
  };

  const handleNumberSelect = async (match, side, selectedPoints) => {
    if (!match || !americano) {
      return;
    }

    const otherSidePoints = americano.points - selectedPoints;

    if (otherSidePoints < 0) {
      toast.error("Invalid points selection");
      return;
    }

    const sideAPoints = side === "A" ? selectedPoints : otherSidePoints;
    const sideBPoints = side === "B" ? selectedPoints : otherSidePoints;

    setUploadingResults({
      ...uploadingResults,
      [match.id]: true,
    });
    setInlineGrid(null);

    try {
      // Upload new result (backend will automatically revert existing result if any)
      const response = await Common.ApiService.getInstance().request(
        "UploadAmericanoResult",
        {
          americano_schedule_id: match.id,
          p_plus: sideAPoints,
          p_minus: sideBPoints,
        },
        "Post"
      );

      if (response?.status === respEnum.Success) {
        toast.success("Result uploaded successfully!");

        // Refresh schedule first, then standings
        await getSchedule();
        await new Promise((resolve) => setTimeout(resolve, 300));
        await getStandings();
      } else {
        toast.error(response.message || "Failed to upload result");
      }
    } catch (error) {
      console.error("Error uploading result:", error);
      toast.error(error.message || "Error uploading result");
    } finally {
      setUploadingResults({
        ...uploadingResults,
        [match.id]: false,
      });
    }
  };

  const handleCloseInlineGrid = () => {
    setInlineGrid(null);
  };

  const getTypeName = (type) => {
    if (type === AmericanoTypeEnum.Americano) return "Americano";
    if (type === AmericanoTypeEnum.Maxicano) return "Maxicano";
    return "-";
  };

  const getAccessTypeName = (accessType) => {
    if (accessType === AmericanoAccessTypeEnum.NoAccess) return "No Access";
    if (accessType === AmericanoAccessTypeEnum.FullAccess) return "Full Access";
    return "-";
  };

  const getResultTypeName = (type) => {
    const types = { 1: "Win", 2: "Loss", 3: "Tie", 4: "No Result" };
    return types[type] || "-";
  };

  const getSidePlayers = (players, side) => {
    return players?.filter((p) => p.side === side) || [];
  };

  if (loading) {
    return (
      <div className="container mx-auto p-4">
        <Card>
          <CardContent className="p-6 flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Icon
                icon="svg-spinners:3-dots-fade"
                className="w-8 h-8 mx-auto mb-2"
              />
              <p className="text-gray-500">Loading...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!americano) {
    return null;
  }

  return (
    <div className="container mx-auto p-4 space-y-6">
      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {[
            {
              id: "overview",
              label: "Overview",
              icon: "heroicons:information-circle",
            },
            { id: "schedule", label: "Schedule", icon: "heroicons:calendar" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
                activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Icon icon={tab.icon} className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="space-y-4">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-blue-600 font-medium">
                      Total Matches
                    </p>
                    <p className="text-2xl font-bold text-blue-900 mt-1">
                      {americano.total_matches || 0}
                    </p>
                  </div>
                  <Icon
                    icon="heroicons:calendar-days"
                    className="h-10 w-10 text-blue-400"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-600 font-medium">
                      Duration
                    </p>
                    <p className="text-2xl font-bold text-green-900 mt-1">
                      {americano.estimated_duration_hours
                        ? `${americano.estimated_duration_hours.toFixed(1)}h`
                        : "-"}
                    </p>
                  </div>
                  <Icon
                    icon="heroicons:clock"
                    className="h-10 w-10 text-green-400"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-purple-600 font-medium">
                      Points/Match
                    </p>
                    <p className="text-2xl font-bold text-purple-900 mt-1">
                      {americano.points || 0}
                    </p>
                  </div>
                  <Icon
                    icon="heroicons:star"
                    className="h-10 w-10 text-purple-400"
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-orange-600 font-medium">
                      Matches/Player
                    </p>
                    <p className="text-2xl font-bold text-orange-900 mt-1">
                      {americano.matches_per_players || 0}
                    </p>
                  </div>
                  <Icon
                    icon="heroicons:user-group"
                    className="h-10 w-10 text-orange-400"
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Basic Info */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                  <Icon
                    icon="heroicons:information-circle"
                    className="h-5 w-5 text-blue-600"
                  />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Basic Information
                  </h3>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">
                      Name
                    </label>
                    <p className="text-sm font-semibold text-gray-900">
                      {americano.name || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">
                      Type
                    </label>
                    <p className="text-sm font-semibold text-gray-900">
                      {getTypeName(americano.type)}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">
                      Players
                    </label>
                    <p className="text-sm font-semibold text-gray-900">
                      {americano.no_of_players || 0}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">
                      Courts
                    </label>
                    <p className="text-sm font-semibold text-gray-900">
                      {americano.no_of_courts || 0}
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-gray-500 mb-1 block">
                      Points/Match
                    </label>
                    <p className="text-sm font-semibold text-gray-900">
                      {americano.points || 0}
                    </p>
                  </div>
                  {americano.createdon && (
                    <div>
                      <label className="text-xs font-medium text-gray-500 mb-1 block">
                        Created On
                      </label>
                      <p className="text-sm font-semibold text-gray-900">
                        {moment(americano.createdon).format("DD MMM YYYY")}
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Courts */}
            <Card className="border-0 shadow-md">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-200">
                  <Icon
                    icon="heroicons:map-pin"
                    className="h-5 w-5 text-blue-600"
                  />
                  <h3 className="text-lg font-semibold text-gray-900">
                    Courts
                  </h3>
                  <span className="ml-auto text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                    {americano.courts?.length || 0}
                  </span>
                </div>
                {americano.courts && americano.courts.length > 0 ? (
                  <div className="space-y-2">
                    {americano.courts.map((court, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg hover:bg-gray-100 hover:border-blue-300 transition-all"
                      >
                        <div className="flex items-center justify-center w-10 h-10 bg-blue-500 text-white rounded-lg shrink-0">
                          <span className="font-bold text-sm">{index + 1}</span>
                        </div>
                        <p className="font-semibold text-sm text-gray-900 flex-1">
                          {court.court_name}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon
                      icon="heroicons:map-pin"
                      className="h-8 w-8 mx-auto text-gray-300 mb-2"
                    />
                    <p className="text-sm text-gray-400">No courts assigned</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Schedule Tab */}
      {activeTab === "schedule" && (
        <div className="space-y-4">
          {/* Player Standings Section */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-4 bg-[#0C1B3A] rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-white">
                <Icon
                  icon="heroicons:trophy"
                  className="h-5 w-5 text-yellow-400"
                />
                Player Standings
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {standingsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Icon
                      icon="svg-spinners:3-dots-fade"
                      className="w-8 h-8 mx-auto mb-3 text-[#2E55B9]"
                    />
                    <p className="text-sm text-gray-500">
                      Loading standings...
                    </p>
                  </div>
                </div>
              ) : standings.length > 0 ? (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50 hover:bg-gray-50">
                        <TableHead className="w-16 font-semibold text-[#0C1B3A]">
                          Rank
                        </TableHead>
                        <TableHead className="font-semibold text-[#0C1B3A]">
                          Player
                        </TableHead>
                        <TableHead className="text-center font-semibold text-[#0C1B3A]">
                          Wins
                        </TableHead>
                        <TableHead className="text-center font-semibold text-[#0C1B3A]">
                          Ties
                        </TableHead>
                        <TableHead className="text-center font-semibold text-[#0C1B3A]">
                          Losses
                        </TableHead>
                        <TableHead className="text-center font-semibold text-[#0C1B3A]">
                          P+
                        </TableHead>
                        <TableHead className="text-center font-semibold text-[#0C1B3A]">
                          P-
                        </TableHead>
                        <TableHead className="text-center font-semibold text-[#0C1B3A]">
                          Point Diff
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {standings.map((standing, index) => (
                        <TableRow
                          key={standing.player_id}
                          className={`transition-colors ${
                            index < 3
                              ? index === 0
                                ? "bg-gradient-to-r from-yellow-50 to-amber-50 border-l-4 border-yellow-400"
                                : index === 1
                                ? "bg-gradient-to-r from-gray-50 to-slate-50 border-l-4 border-gray-400"
                                : "bg-gradient-to-r from-orange-50 to-amber-50 border-l-4 border-orange-400"
                              : "hover:bg-gray-50"
                          }`}
                        >
                          <TableCell className="font-bold">
                            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-[#2E55B9]/10">
                              <span className="text-sm font-bold text-[#0C1B3A]">
                                {standing.rank || index + 1}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-3">
                              <Avatar className="h-10 w-10 border-2 border-[#2E55B9]/20">
                                <AvatarImage
                                  src={standing.player_image}
                                  alt={standing.player_name}
                                />
                                <AvatarFallback className="bg-[#2E55B9] text-white text-sm font-semibold">
                                  {standing.player_name
                                    ?.charAt(0)
                                    ?.toUpperCase() || "P"}
                                </AvatarFallback>
                              </Avatar>
                              <span className="font-semibold text-sm text-gray-800">
                                {standing.player_name}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-green-100 text-green-700 rounded-lg font-bold text-sm">
                              {standing.wins || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-orange-100 text-orange-700 rounded-lg font-bold text-sm">
                              {standing.ties || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="inline-flex items-center justify-center w-8 h-8 bg-red-100 text-red-700 rounded-lg font-bold text-sm">
                              {standing.losses || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold text-sm text-[#0C1B3A]">
                              {standing.p_plus || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-semibold text-sm text-[#0C1B3A]">
                              {standing.p_minus || 0}
                            </span>
                          </TableCell>
                          <TableCell className="text-center">
                            <span
                              className={`inline-flex items-center justify-center px-3 py-1 rounded-lg font-bold text-sm ${
                                standing.point_difference >= 0
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {standing.point_difference >= 0 ? "+" : ""}
                              {standing.point_difference || 0}
                            </span>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-[#0C1B3A]/10 rounded-full">
                    <Icon
                      icon="heroicons:trophy"
                      className="h-8 w-8 text-[#2E55B9]"
                    />
                  </div>
                  <p className="text-sm font-medium text-gray-600">
                    No standings available yet
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Upload match results to see standings
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Match Schedule Section */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-4 bg-[#0C1B3A] rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-white">
                <Icon icon="heroicons:calendar-days" className="h-5 w-5" />
                Match Schedule
                {schedule.length > 0 && (
                  <span className="ml-auto text-sm font-medium bg-[#2E55B9] px-3 py-1.5 rounded-md">
                    {schedule.filter((m) => m.is_result_uploaded).length} /{" "}
                    {schedule.length} Completed
                  </span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-5">
              {scheduleLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <Icon
                      icon="svg-spinners:3-dots-fade"
                      className="w-8 h-8 mx-auto mb-3 text-indigo-600"
                    />
                    <p className="text-sm text-gray-500">Loading schedule...</p>
                  </div>
                </div>
              ) : schedule.length > 0 ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {schedule.map((match, index) => (
                    <div
                      key={match.id}
                      className={`relative rounded-lg overflow-hidden transition-all duration-200 border ${
                        match.is_result_uploaded &&
                        match.matchResult !== null &&
                        match.matchResult !==
                          AmericanoMatchResultEnum.Not_Uploaded
                          ? "bg-white border-[#2E55B9] shadow-md"
                          : "bg-white border-gray-200 hover:border-[#2E55B9] hover:shadow-md"
                      }`}
                    >
                      {/* Match Header */}
                      <div
                        className={`px-4 py-3 flex items-center justify-between ${
                          match.is_result_uploaded &&
                          match.matchResult !== null &&
                          match.matchResult !==
                            AmericanoMatchResultEnum.Not_Uploaded
                            ? "bg-[#2E55B9]"
                            : "bg-[#0C1B3A]"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-9 h-9 bg-white/15 rounded-lg">
                            <span className="text-white font-bold text-base">
                              {index + 1}
                            </span>
                          </div>
                          <div>
                            <p className="font-semibold text-white text-sm">
                              Round {match.round}
                            </p>
                            <p className="text-white/70 text-xs">
                              {match.court_name}
                            </p>
                          </div>
                        </div>
                        {match.is_result_uploaded &&
                        match.matchResult !== null &&
                        match.matchResult !==
                          AmericanoMatchResultEnum.Not_Uploaded ? (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/20 rounded-md">
                            <Icon
                              icon="heroicons:check-circle-solid"
                              className="h-3.5 w-3.5 text-white"
                            />
                            <span className="text-xs font-medium text-white">
                              Done
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 px-2.5 py-1 bg-white/10 rounded-md">
                            <Icon
                              icon="heroicons:clock"
                              className="h-3.5 w-3.5 text-white/80"
                            />
                            <span className="text-xs font-medium text-white/80">
                              Pending
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Match Content */}
                      <div className="p-4">
                        {/* Teams Section */}
                        <div className="flex items-stretch gap-3">
                          {/* Side A */}
                          <div className="flex-1">
                            {inlineGrid?.matchId === match.id &&
                            inlineGrid?.side === "A" ? (
                              <div className="p-3 bg-[#0C1B3A] rounded-lg shadow-lg">
                                <p className="text-white text-xs mb-2 text-center font-medium break-words">
                                  {getSidePlayers(match.players, "A")
                                    .map((p) => p.player_name)
                                    .join(" & ")}
                                </p>
                                <div className="grid grid-cols-4 gap-1.5">
                                  {Array.from(
                                    { length: (americano?.points || 32) - 15 },
                                    (_, i) => i + 16
                                  ).map((num) => (
                                    <button
                                      key={num}
                                      onClick={() =>
                                        handleNumberSelect(match, "A", num)
                                      }
                                      disabled={uploadingResults[match.id]}
                                      className="h-8 bg-[#2E55B9] hover:bg-[#2E55B9]/80 text-white rounded-md font-semibold text-xs transition-all disabled:opacity-50"
                                    >
                                      {num}
                                    </button>
                                  ))}
                                  <button
                                    onClick={handleCloseInlineGrid}
                                    disabled={uploadingResults[match.id]}
                                    className="h-8 bg-red-500 hover:bg-red-600 text-white rounded-md font-bold text-xs"
                                  >
                                    X
                                  </button>
                                </div>
                                {uploadingResults[match.id] && (
                                  <div className="mt-2 text-center">
                                    <Icon
                                      icon="svg-spinners:3-dots-fade"
                                      className="h-4 w-4 text-white mx-auto"
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div
                                onClick={() => handleTeamClick(match, "A")}
                                className={`h-full p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
                                  match.matchResult ===
                                  AmericanoMatchResultEnum.Side_A_Won
                                    ? "bg-[#2E55B9] text-white border-[#2E55B9] shadow-md"
                                    : "bg-gray-50 border-gray-200 hover:border-[#2E55B9] hover:bg-blue-50"
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <div
                                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                      match.matchResult ===
                                      AmericanoMatchResultEnum.Side_A_Won
                                        ? "bg-white"
                                        : "bg-[#2E55B9]"
                                    }`}
                                  ></div>
                                  <span
                                    className={`text-xs font-semibold uppercase flex-shrink-0 ${
                                      match.matchResult ===
                                      AmericanoMatchResultEnum.Side_A_Won
                                        ? "text-white"
                                        : "text-[#0C1B3A]"
                                    }`}
                                  >
                                    Side A
                                  </span>
                                  {match.matchResult ===
                                    AmericanoMatchResultEnum.Side_A_Won && (
                                    <Icon
                                      icon="heroicons:trophy-solid"
                                      className="h-4 w-4 text-yellow-300 flex-shrink-0 ml-1"
                                    />
                                  )}
                                </div>
                                <p
                                  className={`font-medium text-sm break-words leading-tight ${
                                    match.matchResult ===
                                    AmericanoMatchResultEnum.Side_A_Won
                                      ? "text-white"
                                      : "text-gray-800"
                                  }`}
                                >
                                  {getSidePlayers(match.players, "A")
                                    .map((p) => p.player_name)
                                    .join(" & ")}
                                </p>
                                {match.is_result_uploaded &&
                                  match.matchResult !== null &&
                                  match.matchResult !==
                                    AmericanoMatchResultEnum.Not_Uploaded &&
                                  match.side_a_result && (
                                    <div
                                      className={`mt-2 text-center py-1.5 rounded-md ${
                                        match.matchResult ===
                                        AmericanoMatchResultEnum.Side_A_Won
                                          ? "bg-white/20"
                                          : "bg-[#2E55B9]/10"
                                      }`}
                                    >
                                      <span
                                        className={`text-lg font-bold ${
                                          match.matchResult ===
                                          AmericanoMatchResultEnum.Side_A_Won
                                            ? "text-white"
                                            : "text-[#2E55B9]"
                                        }`}
                                      >
                                        {match.side_a_result || 0}
                                      </span>
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>

                          {/* VS Divider */}
                          <div className="flex flex-col items-center justify-center px-2">
                            <div className="w-px h-8 bg-gray-200"></div>
                            <div className="my-1 px-2 py-0.5 bg-gray-100 rounded-full">
                              <span className="text-xs font-bold text-gray-500">
                                VS
                              </span>
                            </div>
                            <div className="w-px h-8 bg-gray-200"></div>
                          </div>

                          {/* Side B */}
                          <div className="flex-1">
                            {inlineGrid?.matchId === match.id &&
                            inlineGrid?.side === "B" ? (
                              <div className="p-3 bg-[#0C1B3A] rounded-lg shadow-lg">
                                <p className="text-white text-xs mb-2 text-center font-medium break-words">
                                  {getSidePlayers(match.players, "B")
                                    .map((p) => p.player_name)
                                    .join(" & ")}
                                </p>
                                <div className="grid grid-cols-4 gap-1.5">
                                  {Array.from(
                                    { length: (americano?.points || 32) - 15 },
                                    (_, i) => i + 16
                                  ).map((num) => (
                                    <button
                                      key={num}
                                      onClick={() =>
                                        handleNumberSelect(match, "B", num)
                                      }
                                      disabled={uploadingResults[match.id]}
                                      className="h-8 bg-[#2E55B9] hover:bg-[#2E55B9]/80 text-white rounded-md font-semibold text-xs transition-all disabled:opacity-50"
                                    >
                                      {num}
                                    </button>
                                  ))}
                                  <button
                                    onClick={handleCloseInlineGrid}
                                    disabled={uploadingResults[match.id]}
                                    className="h-8 bg-red-500 hover:bg-red-600 text-white rounded-md font-bold text-xs"
                                  >
                                    X
                                  </button>
                                </div>
                                {uploadingResults[match.id] && (
                                  <div className="mt-2 text-center">
                                    <Icon
                                      icon="svg-spinners:3-dots-fade"
                                      className="h-4 w-4 text-white mx-auto"
                                    />
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div
                                onClick={() => handleTeamClick(match, "B")}
                                className={`h-full p-3 rounded-lg cursor-pointer transition-all duration-200 border ${
                                  match.matchResult ===
                                  AmericanoMatchResultEnum.Side_B_Won
                                    ? "bg-[#2E55B9] text-white border-[#2E55B9] shadow-md"
                                    : "bg-gray-50 border-gray-200 hover:border-[#2E55B9] hover:bg-blue-50"
                                }`}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <div
                                    className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                      match.matchResult ===
                                      AmericanoMatchResultEnum.Side_B_Won
                                        ? "bg-white"
                                        : "bg-[#2E55B9]"
                                    }`}
                                  ></div>
                                  <span
                                    className={`text-xs font-semibold uppercase flex-shrink-0 ${
                                      match.matchResult ===
                                      AmericanoMatchResultEnum.Side_B_Won
                                        ? "text-white"
                                        : "text-[#0C1B3A]"
                                    }`}
                                  >
                                    Side B
                                  </span>
                                  {match.matchResult ===
                                    AmericanoMatchResultEnum.Side_B_Won && (
                                    <Icon
                                      icon="heroicons:trophy-solid"
                                      className="h-4 w-4 text-yellow-300 flex-shrink-0 ml-1"
                                    />
                                  )}
                                </div>
                                <p
                                  className={`font-medium text-sm break-words leading-tight ${
                                    match.matchResult ===
                                    AmericanoMatchResultEnum.Side_B_Won
                                      ? "text-white"
                                      : "text-gray-800"
                                  }`}
                                >
                                  {getSidePlayers(match.players, "B")
                                    .map((p) => p.player_name)
                                    .join(" & ")}
                                </p>
                                {match.is_result_uploaded &&
                                  match.matchResult !== null &&
                                  match.matchResult !==
                                    AmericanoMatchResultEnum.Not_Uploaded &&
                                  match.side_b_result && (
                                    <div
                                      className={`mt-2 text-center py-1.5 rounded-md ${
                                        match.matchResult ===
                                        AmericanoMatchResultEnum.Side_B_Won
                                          ? "bg-white/20"
                                          : "bg-[#2E55B9]/10"
                                      }`}
                                    >
                                      <span
                                        className={`text-lg font-bold ${
                                          match.matchResult ===
                                          AmericanoMatchResultEnum.Side_B_Won
                                            ? "text-white"
                                            : "text-[#2E55B9]"
                                        }`}
                                      >
                                        {match.side_b_result || 0}
                                      </span>
                                    </div>
                                  )}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Match Result Banner */}
                        {match.is_result_uploaded &&
                          match.matchResult !== null &&
                          match.matchResult !==
                            AmericanoMatchResultEnum.Not_Uploaded && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              {match.matchResult ===
                                AmericanoMatchResultEnum.Side_A_Won && (
                                <div className="flex items-center justify-center gap-2 py-2.5 px-4 bg-[#2E55B9] rounded-lg">
                                  <Icon
                                    icon="heroicons:trophy-solid"
                                    className="h-5 w-5 text-yellow-300"
                                  />
                                  <span className="font-bold text-white text-sm">
                                    Side A Wins!
                                  </span>
                                  <span className="text-white/90 text-sm font-semibold ml-2 bg-white/20 px-2 py-0.5 rounded">
                                    {match.side_a_result || 0} -{" "}
                                    {match.side_b_result || 0}
                                  </span>
                                </div>
                              )}
                              {match.matchResult ===
                                AmericanoMatchResultEnum.Side_B_Won && (
                                <div className="flex items-center justify-center gap-2 py-2.5 px-4 bg-[#2E55B9] rounded-lg">
                                  <Icon
                                    icon="heroicons:trophy-solid"
                                    className="h-5 w-5 text-yellow-300"
                                  />
                                  <span className="font-bold text-white text-sm">
                                    Side B Wins!
                                  </span>
                                  <span className="text-white/90 text-sm font-semibold ml-2 bg-white/20 px-2 py-0.5 rounded">
                                    {match.side_b_result || 0} -{" "}
                                    {match.side_a_result || 0}
                                  </span>
                                </div>
                              )}
                              {match.matchResult ===
                                AmericanoMatchResultEnum.Tied && (
                                <div className="flex items-center justify-center gap-2 py-2.5 px-4 bg-[#0C1B3A] rounded-lg">
                                  <Icon
                                    icon="heroicons:scale"
                                    className="h-5 w-5 text-white"
                                  />
                                  <span className="font-bold text-white text-sm">
                                    Match Tied
                                  </span>
                                  <span className="text-white/90 text-sm font-semibold ml-2 bg-white/20 px-2 py-0.5 rounded">
                                    {match.side_a_result || 0} -{" "}
                                    {match.side_b_result || 0}
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon
                    icon="heroicons:calendar-days"
                    className="h-16 w-16 mx-auto text-gray-300 mb-4"
                  />
                  <p className="text-gray-500 text-lg font-medium">
                    No schedule generated yet
                  </p>
                  <p className="text-gray-400 text-sm mt-1">
                    Schedule will be generated when the americano is created
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default AmericanoDetails;
